import { useQuery } from 'react-query';
import { useDebounceValue } from 'usehooks-ts';
import {
  detectMapServiceType,
  type IServiceDetectionResult,
  isDetectableLayerType,
  type ServiceDetectionStatus,
} from '../../layers/detection/detectMapServiceType.ts';
import {
  type IMapLayerSource,
  type MapLayerType,
} from '../../layers/MapLayer.ts';
import { isIntrospectableLayerUrl } from '../../metadata/fetchLayerMetadata.ts';

export type ServiceTypeValidationStatus =
  | 'idle'
  | 'loading'
  | ServiceDetectionStatus;

export interface IMapServiceTypeValidationOptions {
  /**
   * Consente al chiamante di sospendere la verifica (es. quando
   * l'introspezione ha già confermato il servizio). Default: true.
   */
  enabled?: boolean;
}

const DETECTION_STALE_TIME = 10 * 60 * 1000;
const DETECTION_CACHE_TIME = 30 * 60 * 1000;

/**
 * Verifica non bloccante che il servizio all'URL configurato risponda
 * davvero come il tipo di layer scelto. Per i tipi non verificabili
 * (OSM, XYZ) o gli URL non validi resta in `idle` senza richieste.
 */
export function useMapServiceTypeValidation(
  source: IMapLayerSource,
  options: IMapServiceTypeValidationOptions = {},
): { status: ServiceTypeValidationStatus; result?: IServiceDetectionResult } {
  const trimmedUrl = source.url?.trim() ?? '';
  const [debouncedUrl] = useDebounceValue(trimmedUrl, 300);

  const enabled =
    (options.enabled ?? true) &&
    isDetectableLayerType(source.type) &&
    isIntrospectableLayerUrl(debouncedUrl);

  const query = useQuery(
    ['map-service-detection', source.type, debouncedUrl],
    () => detectMapServiceType(debouncedUrl, source.type as MapLayerType),
    {
      enabled,
      staleTime: DETECTION_STALE_TIME,
      cacheTime: DETECTION_CACHE_TIME,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  if (!enabled) return { status: 'idle' };
  if (query.isLoading || debouncedUrl !== trimmedUrl) {
    return { status: 'loading' };
  }
  if (!query.data) return { status: 'loading' };

  return { status: query.data.status, result: query.data };
}
